/**
 * <omnibus-element>
 *
 * 1. Pokazuje historię ceny (Omnibus) dla aktywnego wariantu.
 * 2. Jeśli aplikacja BSS B2B Solution wstrzyknie do `.product-info`
 *    ceny netto / brutto (`.bss-b2b-price-no-vat` + `.bss-b2b-price-has-vat`),
 *    obliczamy współczynnik VAT i renderujemy w Omnibusie zarówno netto,
 *    jak i brutto – w tym samym formacie waluty, który został wyrenderowany
 *    przez Liquid (różne rynki: `.` vs `,`, spacje / nbsp jako separator tysięcy, itd.).
 */

const OMNIBUS_NET_FALLBACK = 'netto';
const OMNIBUS_GROSS_FALLBACK = 'brutto';

/**
 * Wyciąga liczbę z dowolnego tekstu z ceną.
 * Odporne na:
 *  - symbole i słowa walutowe ("zł", "€", "PLN", "excl. VAT", "incl. VAT", ...)
 *  - spacje zwykłe i nierozdzielające (\u00A0) jako separator tysięcy
 *  - różne separatory dziesiętne (`.` lub `,`)
 *  - mieszane formatowanie ("1,234.56" vs "1 234,56" vs "1234,5")
 */
function parseOmnibusAmount(input) {
  if (input == null) return NaN;
  const cleaned = String(input)
    .replace(/\u00A0/g, ' ')
    .replace(/[^\d.,\-\s]/g, '')
    .trim();
  if (!cleaned) return NaN;

  const noSpaces = cleaned.replace(/\s+/g, '');
  if (!noSpaces || /^-?$/.test(noSpaces)) return NaN;

  const lastDot = noSpaces.lastIndexOf('.');
  const lastComma = noSpaces.lastIndexOf(',');

  let decimalSep = null;

  if (lastDot !== -1 && lastComma !== -1) {
    decimalSep = lastDot > lastComma ? '.' : ',';
  } else if (lastDot !== -1) {
    const after = noSpaces.length - lastDot - 1;
    decimalSep = after === 1 || after === 2 ? '.' : null;
  } else if (lastComma !== -1) {
    const after = noSpaces.length - lastComma - 1;
    decimalSep = after === 1 || after === 2 ? ',' : null;
  }

  let normalized;
  if (decimalSep) {
    const thousandSep = decimalSep === '.' ? ',' : '.';
    normalized = noSpaces.split(thousandSep).join('').replace(decimalSep, '.');
  } else {
    normalized = noSpaces.replace(/[.,]/g, '');
  }

  const num = Number.parseFloat(normalized);
  return Number.isFinite(num) ? num : NaN;
}

/**
 * Zwraca string z tą samą ceną, ale przeformatowaną – zachowując ten sam
 * styl separatorów co `template`. `newAmount` to liczba w jednostkach głównych (np. zł).
 */
function formatOmnibusLikeTemplate(template, newAmount) {
  if (!Number.isFinite(newAmount)) return template;

  const re = /-?\d[\d .,\u00A0]*\d|-?\d/;
  const match = template.match(re);
  if (!match) return template;

  const matched = match[0];
  const startsMinus = matched.startsWith('-');
  const raw = matched.replace(/^-/, '').replace(/\u00A0/g, ' ');

  const lastDot = raw.lastIndexOf('.');
  const lastComma = raw.lastIndexOf(',');
  const hasSpace = /\s/.test(raw);

  let decimalSep = null;
  let thousandSep = null;

  if (lastDot !== -1 && lastComma !== -1) {
    decimalSep = lastDot > lastComma ? '.' : ',';
    thousandSep = decimalSep === '.' ? ',' : '.';
  } else if (lastDot !== -1) {
    const after = raw.length - lastDot - 1;
    if (after === 1 || after === 2) {
      decimalSep = '.';
      thousandSep = hasSpace ? ' ' : null;
    } else {
      thousandSep = '.';
    }
  } else if (lastComma !== -1) {
    const after = raw.length - lastComma - 1;
    if (after === 1 || after === 2) {
      decimalSep = ',';
      thousandSep = hasSpace ? ' ' : null;
    } else {
      thousandSep = ',';
    }
  } else if (hasSpace) {
    thousandSep = ' ';
  }

  let decimals;
  if (decimalSep) {
    const idx = decimalSep === '.' ? lastDot : lastComma;
    decimals = raw.length - idx - 1;
  } else {
    decimals = 0;
  }

  const abs = Math.abs(newAmount);
  const fixed = abs.toFixed(decimals);
  const parts = fixed.split('.');
  let intPart = parts[0];
  const decPart = parts[1];

  if (thousandSep) {
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
  }

  const numberStr = (startsMinus ? '-' : '') + (decPart ? intPart + (decimalSep || '.') + decPart : intPart);
  return template.replace(matched, numberStr);
}

class Omnibus extends HTMLElement {
  constructor() {
    super();
    this._bssObserver = null;
    this._vatRatio = null;
    this._onVariantChange = this._onVariantChange.bind(this);
    this._tryApplyVat = this._tryApplyVat.bind(this);
  }

  connectedCallback() {
    this.productInfo = this.closest('.product-info');
    this.priceElements = this.querySelectorAll('[data-prices-variant-id]');

    if (!this.productInfo || !this.priceElements.length) {
      return;
    }

    this.netLabel = (window.Eurus && window.Eurus.price_net_label) || OMNIBUS_NET_FALLBACK;
    this.grossLabel = (window.Eurus && window.Eurus.price_gross_label) || OMNIBUS_GROSS_FALLBACK;

    this.productInfo.addEventListener('variant:change', this._onVariantChange);

    // VAT nie zmienia się między wariantami – wystarczy pobrać go raz.
    // Próbujemy od razu (może BSS już wstrzyknął ceny), a jeśli jeszcze nie,
    // czekamy z observerem do pierwszego sukcesu i potem się rozłączamy.
    if (!this._tryApplyVat()) {
      this._setupBssObserver();
    }
  }

  disconnectedCallback() {
    if (this.productInfo) {
      this.productInfo.removeEventListener('variant:change', this._onVariantChange);
    }
    this._disconnectObserver();
  }

  _onVariantChange(event) {
    const variantId = event.detail?.variant?.id;
    this.updateVisibility(variantId);
    // Przy zmianie wariantu Liquid nadal pokazuje netto – od razu nakładamy
    // zapamiętany ratio na nowo widoczny blok (bez ponownego czytania DOM BSS).
    if (this._vatRatio) this._applyVat(this._vatRatio);
  }

  updateVisibility(variantId) {
    if (!variantId) return;
    this.priceElements.forEach((element) => {
      const priceVariantId = element.dataset.pricesVariantId;
      if (String(variantId) === String(priceVariantId)) {
        element.style.display = 'block';
      } else {
        element.style.display = 'none';
      }
    });
  }

  _setupBssObserver() {
    if (!('MutationObserver' in window)) return;
    this._bssObserver = new MutationObserver(() => {
      if (this._tryApplyVat()) {
        this._disconnectObserver();
      }
    });
    this._bssObserver.observe(this.productInfo, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  _disconnectObserver() {
    if (this._bssObserver) {
      this._bssObserver.disconnect();
      this._bssObserver = null;
    }
  }

  /**
   * Znajduje w `.product-info` wstrzyknięte przez BSS ceny netto/brutto
   * (w tym samym rodzicu) i zwraca współczynnik VAT (gross/net) albo null.
   */
  _getVatRatio() {
    if (!this.productInfo) return null;

    const netNodes = this.productInfo.querySelectorAll('.bss-b2b-price-no-vat');
    for (const netNode of netNodes) {
      const parent = netNode.parentElement;
      if (!parent) continue;
      const grossNode = parent.querySelector('.bss-b2b-price-has-vat');
      if (!grossNode) continue;

      const net = parseOmnibusAmount(netNode.textContent);
      const gross = parseOmnibusAmount(grossNode.textContent);
      if (!Number.isFinite(net) || !Number.isFinite(gross) || net <= 0 || gross <= 0) {
        continue;
      }
      return gross / net;
    }
    return null;
  }

  _tryApplyVat() {
    const ratio = this._getVatRatio();
    // Dopuszczamy ratio === 1 (VAT 0%, np. VIES / produkt zwolniony):
    // brutto == netto, ale nadal pokazujemy obie etykiety z tą samą ceną.
    if (!ratio || !Number.isFinite(ratio) || ratio < 1 || ratio > 3) return false;
    this._vatRatio = ratio;
    this._applyVat(ratio);
    return true;
  }

  _applyVat(ratio) {
    // Tolerancja na b\u0142\u0119dy zaokr\u0105glenia (np. "100,00" vs "100,01")
    const noVat = Math.abs(ratio - 1) < 0.005;

    this.querySelectorAll('.omnibus-price').forEach((wrap) => {
      const netEl = wrap.querySelector('.omnibus-price-net');
      const grossEl = wrap.querySelector('.omnibus-price-gross');
      if (!netEl || !grossEl) return;

      // Pierwotny tekst ceny (z Liquid) \u2013 \u017ceby kolejne wywo\u0142ania nie kumulowa\u0142y etykiet.
      if (!netEl.dataset.omnibusNetTemplate) {
        netEl.dataset.omnibusNetTemplate = netEl.textContent;
      }
      const netTemplate = netEl.dataset.omnibusNetTemplate;

      // VAT 0% \u2192 pokazujemy sam\u0105 cen\u0119, bez etykiet netto/brutto.
      if (noVat) {
        if (netEl.textContent !== netTemplate) netEl.textContent = netTemplate;
        if (grossEl.textContent !== '') grossEl.textContent = '';
        if (!grossEl.hasAttribute('hidden')) grossEl.setAttribute('hidden', '');
        return;
      }

      // Preferujemy exact cents z Liquid \u2013 precyzyjne mno\u017cenie przez VAT.
      const rawCents = Number.parseInt(wrap.dataset.omnibusPrice, 10);
      const netAmount = Number.isFinite(rawCents) ? rawCents / 100 : parseOmnibusAmount(netTemplate);
      if (!Number.isFinite(netAmount)) return;

      const grossFormatted = formatOmnibusLikeTemplate(netTemplate, netAmount * ratio);

      const netWithLabel = `${netTemplate} ${this.netLabel}`;
      const grossWithLabel = ` / ${grossFormatted} ${this.grossLabel}`;

      if (netEl.textContent !== netWithLabel) netEl.textContent = netWithLabel;
      if (grossEl.textContent !== grossWithLabel) grossEl.textContent = grossWithLabel;
      if (grossEl.hasAttribute('hidden')) grossEl.removeAttribute('hidden');
    });
  }
}

customElements.define('omnibus-element', Omnibus);

